// src/components/CompactBookingForm/index.js
import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useModal } from "context";
import { useUTM } from "context/UTMContext";
import { selectIsAuthenticated, selectUserType } from "store/auth/selectors";
import Login from "modals/Login";
import RequestQuotationForm from "modals/RequestQuotationForm";
import { emptyCart } from "services/basket.services";
import { postBookingStepByStep, validateSlot, fetchPlaceDates } from "services/booking.services";
import PricesTable from "reusableComponents/PricesTable";
import useQueryParam from "hooks/useQueryParam";
import { 
  formatDateToString, 
  formatDateForDisplay,
  createStableLocalDate,
  isSameDate,
  getDurationOptions,
  timeToHour,
  hourToTime,
  getPossibleDayValues,
  parseBinaryToTimeSlots,
  formatTimeDisplay
} from "../utils/dateTimeHelpers";
import styles from "./styles.module.scss";

const SelectedSlotsSummary = memo(({ slotCount, t }) => {
  if (slotCount === 0) return null;
  
  return (
    <span className={styles.selectedDate}>
      <span className={styles.dateDisplay}>
        {t("booking.compactBookingForm.slotsSelected", { count: slotCount })}
        <span className={styles.slotsSummaryBadge}> ✓</span>
      </span>
    </span>
  );
});
SelectedSlotsSummary.displayName = 'SelectedSlotsSummary';

// Helper to calculate duration in hours
const calculateDuration = (from, to) => {
  if (!from || !to) return 0;
  const fromHour = timeToHour(from);
  const toHour = timeToHour(to);
  return Math.round((toHour - fromHour) * 10) / 10;
};

const CompactBookingForm = ({ space, className, isModal = false, onBookingStart, onBookingEnd }) => {
  const { t, i18n } = useTranslation("global");
  const navigate = useNavigate();
  const { utmParams, addUTMsToUrl } = useUTM();
  const { openModal, closeModal } = useModal();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const userType = useSelector(selectUserType);
  const token = useSelector(state => state.auth.token);
  const user = useSelector(state => state.auth.currentUser);
  const bookingFilters = useSelector((state) => state.filters);
  const { from: filterFromDate } = useSelector((state) => state.filters);
  const isTrainee = user?.trainee === 1;
  
  // ============================================================
  // ADDED: Get externalID from URL
  // ============================================================
  const externalID = useQueryParam("externalID");

  // ============================================================
  // FIX: Correct opening_hours from binary data when space prop changes
  // ============================================================
  const [correctedSpace, setCorrectedSpace] = useState(null);
  
  useEffect(() => {
    if (!space) {
      setCorrectedSpace(null);
      return;
    }
    
    if (space._openingHoursCorrected) {
      setCorrectedSpace(space);
      return;
    }
    
    const hasBinaryData = [0, 1, 2, 3, 4, 5, 6].some(i => space[`day_${i}`]);
    if (!hasBinaryData || !space.opening_hours) {
      setCorrectedSpace(space);
      return;
    }
    
    const correctedOpeningHours = [];
    for (let i = 0; i < 7; i++) {
      const dayKey = `day_${i}`;
      const binaryStr = space[dayKey];
      if (binaryStr) {
        const slots = parseBinaryToTimeSlots(binaryStr);
        slots.forEach(slot => {
          correctedOpeningHours.push({
            day: i,
            from: slot.from + ':00',
            to: slot.to + ':00'
          });
        });
      }
    }
    
    if (correctedOpeningHours.length > 0) {
      const corrected = {
        ...space,
        opening_hours: correctedOpeningHours,
        _openingHoursCorrected: true
      };
      setCorrectedSpace(corrected);
    } else {
      setCorrectedSpace(space);
    }
  }, [space]);
  
  const effectiveSpace = correctedSpace || space;

  // ============================================================
  // Form State with booking_hours array
  // ============================================================
  const [formData, setFormData] = useState(() => {
    return {
      place_id: space?.id,
      externalID: externalID,
      capacity: Number(bookingFilters?.capacity) || 1,
      coworking_stations: Number(bookingFilters?.coworking_stations) || 0,
      booking_hours: [],
      day_services: [],
      comment: "",
    };
  });

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [capacity, setCapacity] = useState(() => {
    if (bookingFilters?.capacity && bookingFilters.capacity > 0) {
      return bookingFilters.capacity;
    }
    return 1;
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [isAddingSlot, setIsAddingSlot] = useState(false);
  
  // ============================================================
  // ADDED: Booking step state for three-state flow
  // ============================================================
  const [bookingStep, setBookingStep] = useState('initial');
  
  // ============================================================
  // CRITICAL FIX: Loading state is initialized from sessionStorage
  // ============================================================
  const [isProcessing, setIsProcessing] = useState(() => {
    return sessionStorage.getItem('booking_form_processing') === 'true';
  });
  const [processingMessage, setProcessingMessage] = useState(() => {
    return sessionStorage.getItem('booking_form_processing_message') || "";
  });
  const [processingStep, setProcessingStep] = useState(0);

  const calendarRef = useRef(null);
  const isExecutingRef = useRef(false);
  const isNavigatingRef = useRef(false);
  const isMountedRef = useRef(true);
  const isCreatingRef = useRef(false);
  const isInitializedRef = useRef(false);
  const pendingDataRef = useRef(null);
  const hasTriggeredProcessorRef = useRef(false);
  const isProcessingCompleteRef = useRef(false);
  const isBookingCompleteRef = useRef(false);
  const bookingSuccessfullyCompletedRef = useRef(false);
  const bookingCompletedRef = useRef(false);
  const cleanupTimeoutRef = useRef(null);
  const processedPendingRef = useRef(false);
  const isFromPendingFlowRef = useRef(false);
  // ── PREFILL FIX ── Guard so the snapshot is materialized once per mount
  const prefillMaterializedRef = useRef(false);

  const BOOKING_COMPLETED_SUCCESS_KEY = `booking_completed_success_${effectiveSpace?.id || space?.id}`;
  const RESTORATION_DONE_KEY = `restoration_done_${effectiveSpace?.id || space?.id}`;
  const SUBMITTED_FORM_KEY = `booking_submitted_form_${effectiveSpace?.id || space?.id}`;

  if (sessionStorage.getItem(BOOKING_COMPLETED_SUCCESS_KEY) === 'true') {
    bookingSuccessfullyCompletedRef.current = true;
  }

  const isCoworking = effectiveSpace?.placeUsage?.key === "coworking";
  const maxCapacity = isCoworking ? effectiveSpace?.coworking_stations : effectiveSpace?.max_capacity;
  const durationOptions = getDurationOptions(effectiveSpace?.minimal_duration, t);
  const currentLanguage = i18n.language || t("locale", "en");

  const STORAGE_KEYS = {
    PENDING_DATA: 'pending_booking_data',
    PROCESSING: 'booking_form_processing',
    PROCESSING_MSG: 'booking_form_processing_message',
    BOOKING_SPACE_ID: 'booking_space_id',
    PROCESSING_IN_PROGRESS: 'processing_booking_in_progress',
    FLOW_START_TIME: 'booking_flow_start_time',
    BOOKING_COMPLETED: 'booking_completed',
    PROCESSING_STARTED: 'processing_started',
    JUST_LOGGED_IN: 'just_logged_in_for_booking',
    JUST_REGISTERED: 'just_registered_for_booking',
  };

  // ── PREFILL FIX ── Poll for the snapshot and materialize it into React state.
  // Nothing else reliably re-renders the visible instance after the snapshot
  // is written in the post-login flow, so a lightweight poll is the simplest
  // reliable trigger that doesn't require a page reload. Once materialized,
  // the SUBMITTED_FORM_KEY snapshot is removed so it can't override user edits.
  // NOTE: We deliberately do NOT remove BOOKING_COMPLETED_SUCCESS_KEY here, so
  // that `bookingCompleted` remains true and the loading overlay stays hidden
  // (matching the initial version's behaviour).
  useEffect(() => {
    if (prefillMaterializedRef.current) return;
    if (!effectiveSpace?.id && !space?.id) return;

    const tryMaterialize = () => {
      if (prefillMaterializedRef.current) return true;
      if (sessionStorage.getItem(BOOKING_COMPLETED_SUCCESS_KEY) !== 'true') return false;
      const raw = sessionStorage.getItem(SUBMITTED_FORM_KEY);
      if (!raw) return false;

      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.bookingHours) && parsed.bookingHours.length > 0) {
          setFormData(prev => ({ ...prev, booking_hours: parsed.bookingHours }));
        }
        if (parsed.capacity != null) setCapacity(parsed.capacity);
      } catch (e) {
        console.warn('[CompactBookingForm] Failed to materialize prefill', e);
      }

      // Only remove the snapshot. Keep BOOKING_COMPLETED_SUCCESS_KEY intact
      // so `bookingCompleted` stays true (initial-version behaviour).
      sessionStorage.removeItem(SUBMITTED_FORM_KEY);
      prefillMaterializedRef.current = true;
      return true;
    };

    if (tryMaterialize()) return;

    const interval = setInterval(() => {
      if (tryMaterialize()) clearInterval(interval);
    }, 300);

    // Give up after 15s so we don't poll forever in edge cases.
    const timeout = setTimeout(() => {
      clearInterval(interval);
      prefillMaterializedRef.current = true;
    }, 15000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [effectiveSpace?.id, space?.id, BOOKING_COMPLETED_SUCCESS_KEY, SUBMITTED_FORM_KEY]);

  const handlePhoneClick = () => {
    window.dataLayer.push({
      event: 'clic_appel_telephone_reservation',
      call_source: 'booking_section'
    });

    const handleVisibilityChange = () => {
      if (document.hidden) {
        window.dataLayer.push({
          event: 'call_button_focus_lost',
          call_source: 'booking_section'
        });
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange, { once: true });
    window.location.href = "tel:+33180522522";
  };

  const clearAllBookingData = useCallback(() => {
    console.log('[CompactBookingForm] 🧹 clearAllBookingData called');
    const keysToRemove = [
      STORAGE_KEYS.PENDING_DATA,
      STORAGE_KEYS.PROCESSING,
      STORAGE_KEYS.PROCESSING_MSG,
      STORAGE_KEYS.BOOKING_SPACE_ID,
      STORAGE_KEYS.PROCESSING_IN_PROGRESS,
      STORAGE_KEYS.FLOW_START_TIME,
      STORAGE_KEYS.PROCESSING_STARTED,
      STORAGE_KEYS.JUST_LOGGED_IN,
      STORAGE_KEYS.JUST_REGISTERED,
      STORAGE_KEYS.BOOKING_COMPLETED,
    ];
    keysToRemove.forEach(key => {
      if (sessionStorage.getItem(key)) {
        console.log(`[CompactBookingForm] Clearing: ${key}`);
        sessionStorage.removeItem(key);
      }
    });
    sessionStorage.setItem(RESTORATION_DONE_KEY, 'true');
    processedPendingRef.current = false;
    sessionStorage.removeItem(`processed_pending_${effectiveSpace?.id || space?.id}`);
    setIsProcessing(false);
    setProcessingMessage("");
    setProcessingStep(0);
    isFromPendingFlowRef.current = false;
  }, [effectiveSpace?.id, space?.id, RESTORATION_DONE_KEY]);

  const savePendingData = useCallback((data) => {
    console.log('[CompactBookingForm] 💾 savePendingData called');
    pendingDataRef.current = data;
    sessionStorage.setItem(STORAGE_KEYS.PENDING_DATA, JSON.stringify(data));
    sessionStorage.setItem(STORAGE_KEYS.BOOKING_SPACE_ID, String(data.spaceId));
    sessionStorage.setItem(STORAGE_KEYS.FLOW_START_TIME, Date.now().toString());
    sessionStorage.removeItem(STORAGE_KEYS.BOOKING_COMPLETED);
    sessionStorage.removeItem(BOOKING_COMPLETED_SUCCESS_KEY);
    sessionStorage.removeItem(RESTORATION_DONE_KEY);
    sessionStorage.removeItem(SUBMITTED_FORM_KEY);
    bookingCompletedRef.current = false;
    bookingSuccessfullyCompletedRef.current = false;
    setIsProcessing(false);
    setProcessingMessage("");
    setProcessingStep(0);
    isFromPendingFlowRef.current = true;
  }, [BOOKING_COMPLETED_SUCCESS_KEY, RESTORATION_DONE_KEY, SUBMITTED_FORM_KEY]);

  const getPendingData = useCallback(() => {
    if (pendingDataRef.current) return pendingDataRef.current;
    const stored = sessionStorage.getItem(STORAGE_KEYS.PENDING_DATA);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  }, []);

  const clearPendingData = useCallback(() => {
    console.log('[CompactBookingForm] 🗑️ clearPendingData called');
    pendingDataRef.current = null;
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.BOOKING_SPACE_ID);
    sessionStorage.removeItem(STORAGE_KEYS.FLOW_START_TIME);
    sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_STARTED);
    processedPendingRef.current = false;
    setIsProcessing(false);
    setProcessingMessage("");
    setProcessingStep(0);
    isFromPendingFlowRef.current = false;
  }, []);

  const setProcessingState = useCallback((message) => {
    console.log('[CompactBookingForm] 🔴 SET PROCESSING:', message);
    const isPendingFlow = sessionStorage.getItem(STORAGE_KEYS.JUST_LOGGED_IN) === 'true' || 
                          sessionStorage.getItem(STORAGE_KEYS.JUST_REGISTERED) === 'true' ||
                          sessionStorage.getItem('has_pending_booking_after_oauth') === 'true';
    setIsProcessing(true);
    setProcessingMessage(message);
    isFromPendingFlowRef.current = isPendingFlow;
    sessionStorage.setItem(STORAGE_KEYS.PROCESSING, 'true');
    sessionStorage.setItem(STORAGE_KEYS.PROCESSING_MSG, message);
    sessionStorage.setItem(STORAGE_KEYS.BOOKING_SPACE_ID, String(effectiveSpace?.id || space?.id));
    sessionStorage.setItem(STORAGE_KEYS.PROCESSING_IN_PROGRESS, 'true');
    sessionStorage.setItem(RESTORATION_DONE_KEY, 'true');
    bookingSuccessfullyCompletedRef.current = false;
    bookingCompletedRef.current = false;
    isProcessingCompleteRef.current = false;
    isBookingCompleteRef.current = false;
    sessionStorage.removeItem(BOOKING_COMPLETED_SUCCESS_KEY);
    sessionStorage.removeItem(STORAGE_KEYS.BOOKING_COMPLETED);
  }, [effectiveSpace?.id, space?.id, RESTORATION_DONE_KEY, BOOKING_COMPLETED_SUCCESS_KEY]);

  useEffect(() => {
    if (sessionStorage.getItem(BOOKING_COMPLETED_SUCCESS_KEY) === 'true') {
      bookingSuccessfullyCompletedRef.current = true;
      setIsProcessing(false);
      setProcessingMessage("");
      setProcessingStep(0);
      sessionStorage.setItem(RESTORATION_DONE_KEY, 'true');
      return;
    }
    if (sessionStorage.getItem(RESTORATION_DONE_KEY) === 'true') {
      return;
    }
    const justLoggedIn = sessionStorage.getItem(STORAGE_KEYS.JUST_LOGGED_IN) === 'true';
    const justRegistered = sessionStorage.getItem(STORAGE_KEYS.JUST_REGISTERED) === 'true';
    const isInBookingFlow = justLoggedIn || justRegistered;
    const pendingData = sessionStorage.getItem(STORAGE_KEYS.PENDING_DATA);
    const processingFlag = sessionStorage.getItem(STORAGE_KEYS.PROCESSING) === 'true';
    if (isInBookingFlow && pendingData) {
      if (!processingFlag && !isProcessing) {
        setIsProcessing(false);
        setProcessingMessage("");
        setProcessingStep(0);
      } else if (processingFlag) {
        isFromPendingFlowRef.current = true;
      }
      sessionStorage.setItem(RESTORATION_DONE_KEY, 'true');
      return;
    }
    if (pendingData && processingFlag) {
      const message = sessionStorage.getItem(STORAGE_KEYS.PROCESSING_MSG) || t("booking.compactBookingForm.addingToBasket");
      setIsProcessing(true);
      setProcessingMessage(message);
      setProcessingStep(4);
      isFromPendingFlowRef.current = true;
      sessionStorage.setItem(RESTORATION_DONE_KEY, 'true');
    } else {
      setIsProcessing(false);
      setProcessingMessage("");
      setProcessingStep(0);
      isFromPendingFlowRef.current = false;
      sessionStorage.setItem(RESTORATION_DONE_KEY, 'true');
    }
  }, [t, BOOKING_COMPLETED_SUCCESS_KEY, RESTORATION_DONE_KEY, isProcessing]);

  const formatDateForAPI = (date) => {
    if (!date) return null;
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    const stableDate = createStableLocalDate(date);
    if (stableDate) {
      const year = stableDate.getFullYear();
      const month = String(stableDate.getMonth() + 1).padStart(2, '0');
      const day = String(stableDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return null;
  };

  const getMinAllowedDate = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 2);
    return minDate;
  }, []);

  const isWorkingDay = useCallback((date) => {
    if (!effectiveSpace?.opening_hours || effectiveSpace.opening_hours.length === 0) return false;
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return false;
    const jsDayOfWeek = stableDate.getDay();
    const possibleDayValues = getPossibleDayValues(jsDayOfWeek);
    return effectiveSpace.opening_hours.some(slot => {
      const slotDay = Number(slot.day);
      return possibleDayValues.includes(slotDay);
    });
  }, [effectiveSpace?.opening_hours]);

  const fetchBookingsForDate = useCallback(async (date) => {
    if (!date || !effectiveSpace?.id) return [];
    const formattedDate = formatDateToString(date);
    setLoadingAvailability(true);
    try {
      const response = await fetchPlaceDates(effectiveSpace.id, formattedDate, formattedDate, token);
      if (response?.success && response?.data) {
        const booked = [];
        if (response.data.reservations) {
          response.data.reservations.forEach(res => {
            if (res.date === formattedDate) {
              booked.push({ from: res.from.slice(0, 5), to: res.to.slice(0, 5) });
            }
          });
        }
        if (response.data.private_reservations) {
          response.data.private_reservations.forEach(res => {
            if (formattedDate >= res.start_date && formattedDate <= res.end_date) {
              booked.push({ from: res.from.slice(0, 5), to: res.to.slice(0, 5) });
            }
          });
        }
        return booked;
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setLoadingAvailability(false);
    }
    return [];
  }, [effectiveSpace?.id, token]);

  const isTimeOverlapping = (start, end, bookedSlotsList) => {
    const startHour = timeToHour(start);
    const endHour = timeToHour(end);
    return bookedSlotsList.some(booked => {
      const bookedStart = timeToHour(booked.from);
      const bookedEnd = timeToHour(booked.to);
      return (startHour < bookedEnd && endHour > bookedStart);
    });
  };

  const isOverlappingWithExistingSlots = useCallback((newSlot) => {
    const existingSlots = formData.booking_hours || [];
    return existingSlots.some(slot => {
      if (slot.date !== newSlot.date) return false;
      const start1 = timeToHour(slot.from);
      const end1 = timeToHour(slot.to);
      const start2 = timeToHour(newSlot.from);
      const end2 = timeToHour(newSlot.to);
      return (start1 < end2 && end1 > start2);
    });
  }, [formData.booking_hours]);

  const getDaySchedule = useCallback((date) => {
    if (!date) return null;
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return null;
    const jsDayOfWeek = stableDate.getDay();
    const possibleDayValues = getPossibleDayValues(jsDayOfWeek);
    return effectiveSpace.opening_hours?.find(slot => {
      const slotDay = Number(slot.day);
      return possibleDayValues.includes(slotDay);
    });
  }, [effectiveSpace.opening_hours]);

  const getAvailableTimeSlots = (date, duration, bookedSlotsList) => {
    if (!date || !duration) return [];
    if (!isWorkingDay(date)) return [];
    const daySchedule = getDaySchedule(date);
    if (!daySchedule) return [];
    const openingTime = daySchedule.from.slice(0, 5);
    const closingTime = daySchedule.to.slice(0, 5);
    const closingHour = timeToHour(closingTime);
    const allSlots = [];
    let current = openingTime;
    while (timeToHour(current) + duration <= closingHour) {
      allSlots.push(current);
      const [hours, minutes] = current.split(":").map(Number);
      const nextMinutes = minutes + 30;
      const nextHour = hours + Math.floor(nextMinutes / 60);
      const nextMinute = nextMinutes % 60;
      current = `${nextHour.toString().padStart(2, "0")}:${nextMinute.toString().padStart(2, "0")}`;
    }
    return allSlots.filter(slotStart => {
      const slotEnd = hourToTime(timeToHour(slotStart) + duration);
      return !isTimeOverlapping(slotStart, slotEnd, bookedSlotsList);
    });
  };

  const getAvailableDurationOptions = useCallback(() => {
    if (!selectedDate) return durationOptions;
    const available = [];
    for (const opt of durationOptions) {
      const slots = getAvailableTimeSlots(selectedDate, opt.value, bookedSlots);
      if (slots.length > 0) available.push(opt);
    }
    return available;
  }, [selectedDate, durationOptions, bookedSlots, getAvailableTimeSlots]);

  // ============================================================
  // createBooking - Updated to handle multiple slots
  // ============================================================
  const createBooking = useCallback(async (data, isFromPending = false, wasModal = false) => {
    if (isCreatingRef.current) {
      console.log('[CompactBookingForm] Already creating booking, skipping duplicate');
      return;
    }
    const effectiveIsModal = wasModal || isModal;
    const attemptId = Math.random().toString(36).substring(7);
    console.log(`[CompactBookingForm] 📝 createBooking called. action: ${data.action}, isFromPending: ${isFromPending}`);
    setProcessingState(
      data.action === 'addToBasket' 
        ? t("booking.compactBookingForm.addingToBasket")
        : t("booking.compactBookingForm.preparingReservation")
    );
    await new Promise(resolve => setTimeout(resolve, 50));
    if (effectiveIsModal && onBookingStart) {
      onBookingStart();
    }
    isCreatingRef.current = true;
    try {
      const currentToken = token;
      if (!currentToken) throw new Error('Authentication token missing');
      setProcessingStep(2);
      setProcessingMessage(t("booking.compactBookingForm.clearingSelections"));
      sessionStorage.setItem(STORAGE_KEYS.PROCESSING_MSG, t("booking.compactBookingForm.clearingSelections"));
      try {
        await emptyCart(currentToken);
      } catch (cartError) {
        console.warn('Error emptying cart:', cartError);
      }
      
      let bookingHours = [];
      if (data.bookingHours && data.bookingHours.length > 0) {
        bookingHours = data.bookingHours.map(slot => ({
          date: slot.date,
          from: formatTimeDisplay(slot.from),
          to: formatTimeDisplay(slot.to)
        }));
      } else {
        const formattedDate = data.selectedDateFormatted || formatDateForAPI(data.selectedDate);
        bookingHours = [{
          date: formattedDate,
          from: formatTimeDisplay(data.startTime),
          to: formatTimeDisplay(data.endTime)
        }];
      }
      
      const bookingData = {
        place_id: data.spaceId,
        externalID: externalID,
        capacity: data.isCoworking ? 0 : data.capacity,
        coworking_stations: data.isCoworking ? data.capacity : 0,
        booking_hours: bookingHours,
        ...data.utmParams,
      };
      
      setProcessingStep(3);
      setProcessingMessage(
        data.action === 'addToBasket' 
          ? t("booking.compactBookingForm.addingToBasket")
          : t("booking.compactBookingForm.creatingReservation")
      );
      sessionStorage.setItem(STORAGE_KEYS.PROCESSING_MSG, 
        data.action === 'addToBasket' 
          ? t("booking.compactBookingForm.addingToBasket")
          : t("booking.compactBookingForm.creatingReservation")
      );
      
      const response = await postBookingStepByStep(bookingData, currentToken, 1, null);
      if (response?.id) {
        console.log(`[CompactBookingForm][${attemptId}] ✅ Success! Booking ID:`, response.id);

        if (data.action === 'addToBasket') {
          window.dataLayer.push({
            event: 'add_to_cart',
            space_id: data.spaceId,
            value: response.total_with_vat,
            item_name: effectiveSpace?.name || 'Space',
            currency: 'EUR'
          });
        }

        clearPendingData();
        pendingDataRef.current = null;
        if (data.action === 'addToBasket') {
          console.log(`[CompactBookingForm][${attemptId}] 🛒 Add to Basket flow`);
          bookingSuccessfullyCompletedRef.current = true;
          isBookingCompleteRef.current = true;
          isProcessingCompleteRef.current = true;
          sessionStorage.setItem(BOOKING_COMPLETED_SUCCESS_KEY, 'true');
          sessionStorage.setItem(RESTORATION_DONE_KEY, 'true');
          sessionStorage.setItem(SUBMITTED_FORM_KEY, JSON.stringify({
            bookingHours: bookingHours,
            capacity: data.capacity,
            isCoworking: data.isCoworking,
          }));
          sessionStorage.removeItem(STORAGE_KEYS.PROCESSING);
          sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_MSG);
          sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_IN_PROGRESS);
          sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_STARTED);
          sessionStorage.removeItem(STORAGE_KEYS.JUST_LOGGED_IN);
          sessionStorage.removeItem(STORAGE_KEYS.JUST_REGISTERED);
          if (effectiveIsModal) {
            // closeModal();
          }
          await new Promise(resolve => setTimeout(resolve, 50));
          openModal({
            content: (
              <div className={styles.basketSuccess}>
                <div className={styles.basketSuccessIcon}>✅</div>
                <h3>{t("booking.compactBookingForm.basketAdded")}</h3>
                <p>{t("booking.compactBookingForm.basketSavedForLater")}</p>
                <div className={styles.basketButtons}>
                  <button 
                    className={styles.continueBtn}
                    onClick={() => {
                      closeModal();
                      setIsProcessing(false);
                      setProcessingMessage("");
                      setProcessingStep(0);
                      if (effectiveIsModal && isFromPending && onBookingEnd) {
                        onBookingEnd();
                      }
                    }}
                  >
                    {t("booking.compactBookingForm.continueShopping")}
                  </button>
                  <button 
                    className={styles.viewBasketBtn}
                    onClick={() => {
                      closeModal();
                      setIsProcessing(false);
                      setProcessingMessage("");
                      setProcessingStep(0);
                      if (effectiveIsModal && isFromPending && onBookingEnd) {
                        onBookingEnd();
                      }
                      navigate(addUTMsToUrl("/basket"));
                    }}
                  >
                    {t("booking.compactBookingForm.viewBasket")}
                  </button>
                </div>
              </div>
            ),
            title: t("booking.compactBookingForm.basketAddedSuccess", "Success"),
            size: "small",
            autoClose: false,
            onClose: () => {
              clearAllBookingData();
              setIsProcessing(false);
              setProcessingMessage("");
              setProcessingStep(0);
              if (effectiveIsModal && isFromPending && onBookingEnd) {
                onBookingEnd();
              }
            }
          });
        } else {
          console.log(`[CompactBookingForm][${attemptId}] 📋 Reserve flow - redirecting to booking page`);
          setProcessingStep(4);
          setProcessingMessage(t("booking.compactBookingForm.redirectingToBooking"));
          sessionStorage.setItem(STORAGE_KEYS.PROCESSING_MSG, t("booking.compactBookingForm.redirectingToBooking"));
          sessionStorage.setItem(RESTORATION_DONE_KEY, 'true');
          sessionStorage.removeItem(SUBMITTED_FORM_KEY);
          clearAllBookingData();
          isBookingCompleteRef.current = true;
          bookingSuccessfullyCompletedRef.current = true;
          const bookingUrl = addUTMsToUrl(`/space/${data.spaceId}/book?bookingId=${response.id}`);
          if (effectiveIsModal) {
            closeModal();
          }
          navigate(bookingUrl);
        }
      } else {
        console.log(`[CompactBookingForm][${attemptId}] ❌ Booking creation failed`);
        clearAllBookingData();
        setError(t("booking.compactBookingForm.errorCreationFailed"));
        if (effectiveIsModal && isFromPending && onBookingEnd) {
          onBookingEnd();
        }
      }
    } catch (err) {
      console.error(`[CompactBookingForm][${attemptId}] ❌ Error:`, err);
      clearAllBookingData();
      setError(err.message || t("booking.compactBookingForm.errorCreationFailed"));
      if (effectiveIsModal && isFromPending && onBookingEnd) {
        onBookingEnd();
      }
    } finally {
      isCreatingRef.current = false;
    }
  }, [token, isModal, onBookingStart, onBookingEnd, navigate, t, addUTMsToUrl, 
      setProcessingState, clearAllBookingData, clearPendingData, openModal, closeModal, 
      BOOKING_COMPLETED_SUCCESS_KEY, RESTORATION_DONE_KEY, effectiveSpace?.name, externalID, SUBMITTED_FORM_KEY]);

  // ============================================================
  // PENDING BOOKING PROCESSOR
  // ============================================================
  useEffect(() => {
    const processPending = async () => {
      console.log(`[CompactBookingForm] 🔄 processPending called`);
      
      const hasPendingAfterOAuth = sessionStorage.getItem('has_pending_booking_after_oauth') === 'true';
      
      if (hasPendingAfterOAuth) {
        console.log('[CompactBookingForm] 🔄 Detected Google OAuth flow, processing pending booking');
      }
      
      if (bookingCompletedRef.current || sessionStorage.getItem(STORAGE_KEYS.BOOKING_COMPLETED) === 'true') {
        console.log('[CompactBookingForm] 🚫 Booking already completed, skipping');
        return;
      }
      if (sessionStorage.getItem(BOOKING_COMPLETED_SUCCESS_KEY) === 'true') {
        console.log('[CompactBookingForm] 🚫 Booking was successfully completed, skipping');
        bookingSuccessfullyCompletedRef.current = true;
        return;
      }
      if (processedPendingRef.current) {
        console.log('[CompactBookingForm] 🚫 Pending booking already processed, skipping');
        return;
      }
      
      const pendingData = getPendingData();
      console.log('[CompactBookingForm] 🔍 pendingData:', pendingData ? 'found' : 'not found');
      
      const justLoggedIn = sessionStorage.getItem(STORAGE_KEYS.JUST_LOGGED_IN) === 'true';
      const justRegistered = sessionStorage.getItem(STORAGE_KEYS.JUST_REGISTERED) === 'true';
      const isInBookingFlow = justLoggedIn || justRegistered || hasPendingAfterOAuth;
      
      if (pendingData && token) {
        if (pendingData.spaceId !== (effectiveSpace?.id || space?.id)) {
          console.log(`[CompactBookingForm] Pending data is for different space, clearing`);
          clearPendingData();
          clearAllBookingData();
          return;
        }
        if (!isInBookingFlow) {
          console.log('[CompactBookingForm] 🚫 Not in booking flow, clearing');
          clearPendingData();
          clearAllBookingData();
          return;
        }
        const processingStarted = sessionStorage.getItem(STORAGE_KEYS.PROCESSING_STARTED) === 'true';
        if (processingStarted) {
          console.log('[CompactBookingForm] ⏳ Processing already started, skipping duplicate');
          return;
        }
        const pendingDataString = JSON.stringify(pendingData);
        const processedKey = `processed_pending_${effectiveSpace?.id || space?.id}`;
        if (sessionStorage.getItem(processedKey) === pendingDataString) {
          console.log('[CompactBookingForm] 🚫 This exact pending data was already processed, skipping');
          processedPendingRef.current = true;
          return;
        }
        sessionStorage.setItem(STORAGE_KEYS.PROCESSING_STARTED, 'true');
        const pendingProcessedKey = `pending_processed_${effectiveSpace?.id || space?.id}_${pendingData.timestamp || ''}`;
        if (sessionStorage.getItem(pendingProcessedKey) === 'true') {
          console.log('[CompactBookingForm] 🚫 This pending booking was already processed, clearing');
          clearPendingData();
          clearAllBookingData();
          sessionStorage.removeItem(pendingProcessedKey);
          sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_STARTED);
          processedPendingRef.current = true;
          return;
        }
        
        if (hasPendingAfterOAuth) {
          sessionStorage.removeItem('has_pending_booking_after_oauth');
          console.log('[CompactBookingForm] ✅ Cleared Google OAuth flag');
        }
        
        console.log('[CompactBookingForm] ✅ Processing pending booking after auth');
        hasTriggeredProcessorRef.current = true;
        sessionStorage.setItem(STORAGE_KEYS.PROCESSING_IN_PROGRESS, 'true');
        sessionStorage.setItem(processedKey, pendingDataString);
        processedPendingRef.current = true;
        setProcessingState(
          pendingData.action === 'addToBasket' 
            ? t("booking.compactBookingForm.addingToBasket")
            : t("booking.compactBookingForm.preparingReservation")
        );
        if (pendingData.isModal && onBookingStart) {
          onBookingStart();
        }
        try {
          await createBooking(pendingData, true, pendingData.isModal || false);
          sessionStorage.setItem(pendingProcessedKey, 'true');
          console.log('[CompactBookingForm] ✅ Pending booking processed successfully');
        } catch (error) {
          console.error('[CompactBookingForm] Error processing pending:', error);
          isBookingCompleteRef.current = true;
          clearPendingData();
          clearAllBookingData();
          sessionStorage.removeItem(pendingProcessedKey);
          sessionStorage.removeItem(processedKey);
          processedPendingRef.current = false;
        } finally {
          sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_IN_PROGRESS);
          sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_STARTED);
        }
        return;
      }
      if (sessionStorage.getItem(STORAGE_KEYS.BOOKING_COMPLETED) === 'true') {
        bookingCompletedRef.current = true;
        return;
      }
      if (hasTriggeredProcessorRef.current || isCreatingRef.current) {
        console.log('[CompactBookingForm] Processor already triggered or booking in progress, skipping');
        return;
      }
      if (sessionStorage.getItem(STORAGE_KEYS.PROCESSING_IN_PROGRESS) === 'true') {
        console.log('[CompactBookingForm] Processing already in progress, skipping');
        return;
      }
    };
    if (token && !hasTriggeredProcessorRef.current) {
      processPending();
    }
  }, [token, getPendingData, createBooking, setProcessingState, clearPendingData, 
      clearAllBookingData, effectiveSpace?.id, space?.id, onBookingStart, t, 
      BOOKING_COMPLETED_SUCCESS_KEY]);

  // ============================================================
  // Load availability when date is selected
  // ============================================================
  useEffect(() => {
    if (!selectedDate) return;
    
    const loadAvailability = async () => {
      const booked = await fetchBookingsForDate(selectedDate);
      setBookedSlots(booked);
      
      setSelectedDuration(null);
      setStartTime(null);
      setEndTime(null);
      setAvailableSlots([]);
      setFieldErrors(prev => ({ ...prev, duration: null, startTime: null }));
    };
    
    loadAvailability();
  }, [selectedDate]);

  // ============================================================
  // Update available slots when duration is selected
  // ============================================================
  useEffect(() => {
    if (selectedDate && selectedDuration && bookedSlots.length >= 0) {
      const slots = getAvailableTimeSlots(selectedDate, selectedDuration, bookedSlots);
      setAvailableSlots(slots);
      setStartTime(null);
      setEndTime(null);
    }
  }, [selectedDuration]);

  useEffect(() => {
    if (selectedDate && selectedDuration && bookedSlots.length >= 0) {
      const slots = getAvailableTimeSlots(selectedDate, selectedDuration, bookedSlots);
      setAvailableSlots(slots);
    }
  }, [selectedDuration, selectedDate, bookedSlots]);

  useEffect(() => {
    if (startTime && selectedDuration) {
      const endHour = timeToHour(startTime) + selectedDuration;
      setEndTime(formatTimeDisplay(hourToTime(endHour)));
    } else {
      setEndTime(null);
    }
  }, [startTime, selectedDuration]);

  // ============================================================
  // Initialize with filter date
  // ── PREFILL FIX ── Skip if there's a snapshot so the calendar opens empty
  // ============================================================
  useEffect(() => {
    if (isInitializedRef.current) return;

    const isBasketSuccess = sessionStorage.getItem(BOOKING_COMPLETED_SUCCESS_KEY) === 'true';
    if (isBasketSuccess) {
      isInitializedRef.current = true;
      return;
    }

    if (filterFromDate && !selectedDate) {
      const stableDate = createStableLocalDate(filterFromDate);
      if (stableDate) {
        const minAllowedDate = getMinAllowedDate();
        if (stableDate >= minAllowedDate) {
          setSelectedDate(stableDate);
          setCurrentMonth(stableDate);
          setBookingStep('date-selected');
        }
      }
    }
    isInitializedRef.current = true;
  }, [filterFromDate, selectedDate, getMinAllowedDate, BOOKING_COMPLETED_SUCCESS_KEY]);

  // ============================================================
  // Click outside handler for calendar
  // ============================================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setSelectedDate(null);
        setSelectedDuration(null);
        setStartTime(null);
        setEndTime(null);
        setAvailableSlots([]);
        setBookedSlots([]);
        setIsCalendarOpen(false);
        setBookingStep('initial');
        setFieldErrors(prev => ({ ...prev, date: null, duration: null, startTime: null }));
        setError(null);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[CompactBookingForm] Calendar closed - selections discarded');
        }
      }
    };
    
    if (isCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCalendarOpen]);

  // ============================================================
  // Cleanup
  // ============================================================
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      isNavigatingRef.current = false;
      isExecutingRef.current = false;
      isCreatingRef.current = false;
      hasTriggeredProcessorRef.current = false;
      processedPendingRef.current = false;
    };
  }, []);

  // ============================================================
  // Date/Time Handlers
  // ============================================================
  const handleDateSelect = (date) => {
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return;
    setSelectedDate(stableDate);
    setBookingStep('date-selected');
    setFieldErrors(prev => ({ ...prev, date: null, duration: null, startTime: null }));
    setStartTime(null);
    setEndTime(null);
    setSelectedDuration(null);
    setError(null);
  };

  const handleDurationSelect = (duration) => {
    setSelectedDuration(duration);
    setStartTime(null);
    setEndTime(null);
    setFieldErrors(prev => ({ ...prev, duration: null, startTime: null }));
  };

  const handleStartTimeSelect = (time) => {
    setStartTime(time);
    setBookingStep('time-selected');
    setFieldErrors(prev => ({ ...prev, startTime: null }));
  };

  const handleAddSlot = useCallback(() => {
    if (!selectedDate || !startTime || !endTime) {
      setFieldErrors(prev => ({ ...prev, date: t("booking.compactBookingForm.invalidSlot") }));
      return;
    }

    const formattedStart = formatTimeDisplay(startTime);
    const formattedEnd = formatTimeDisplay(endTime);

    if (!formattedStart || !formattedEnd || 
        formattedStart.length !== 5 || formattedEnd.length !== 5) {
      setError(t("booking.compactBookingForm.invalidSlot"));
      return;
    }

    const newSlot = {
      date: formatDateToString(selectedDate),
      from: formattedStart,
      to: formattedEnd
    };

    if (isOverlappingWithExistingSlots(newSlot)) {
      setError(t("booking.compactBookingForm.slotOverlap"));
      return;
    }

    setIsAddingSlot(true);

    setFormData(prev => ({
      ...prev,
      booking_hours: [...(prev.booking_hours || []), newSlot]
    }));

    setBookingStep('initial');
    setSelectedDate(null);
    setSelectedDuration(null);
    setStartTime(null);
    setEndTime(null);
    setBookedSlots([]);
    setAvailableSlots([]);
    setIsCalendarOpen(false);
    setError(null);
    setIsAddingSlot(false);

    if (formData.day_services && formData.day_services.length > 0) {
      setFormData(prev => ({
        ...prev,
        day_services: []
      }));
    }
  }, [selectedDate, startTime, endTime, isOverlappingWithExistingSlots, t, formData.day_services]);

  const handleRemoveSlot = useCallback((index) => {
    setFormData(prev => {
      const updated = [...(prev.booking_hours || [])];
      updated.splice(index, 1);
      return {
        ...prev,
        booking_hours: updated
      };
    });
    if (formData.booking_hours.length <= 1) {
      setBookingStep('initial');
    }
  }, [formData.booking_hours.length]);

  const availableDurationOptions = getAvailableDurationOptions();

  // ============================================================
  // handleAddToBasketClick - Updated for multiple slots
  // ============================================================
  const handleAddToBasketClick = async () => {
    console.log('[CompactBookingForm] 🛒 handleAddToBasketClick called');
    
    setError(null);
    setFieldErrors({});
    
    bookingCompletedRef.current = false;
    bookingSuccessfullyCompletedRef.current = false;
    isBookingCompleteRef.current = false;
    isProcessingCompleteRef.current = false;
    sessionStorage.removeItem(STORAGE_KEYS.BOOKING_COMPLETED);
    sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_STARTED);
    sessionStorage.removeItem(STORAGE_KEYS.PROCESSING);
    sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_MSG);
    sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_IN_PROGRESS);
    sessionStorage.removeItem(BOOKING_COMPLETED_SUCCESS_KEY);
    sessionStorage.removeItem(RESTORATION_DONE_KEY);
    sessionStorage.removeItem(SUBMITTED_FORM_KEY);
    setIsProcessing(false);
    setProcessingMessage("");
    setProcessingStep(0);
    
    if (!formData.booking_hours || formData.booking_hours.length === 0) {
      setFieldErrors(prev => ({ ...prev, date: t("booking.compactBookingForm.atLeastOneSlotRequired") }));
      return;
    }
    
    for (const slot of formData.booking_hours) {
      if (!slot.date || !slot.from || !slot.to) {
        setFieldErrors(prev => ({ ...prev, date: t("booking.compactBookingForm.invalidSlot") }));
        return;
      }
    }
    
    if (!capacity || capacity < 1) {
      setFieldErrors(prev => ({ ...prev, capacity: t("booking.compactBookingForm.capacityRequired") }));
      return;
    }
    
    if (isTrainee && !effectiveSpace?.trainee_bookable) {
      setError(t("booking.compactBookingForm.traineeNotAllowed"));
      return;
    }
    if (userType === "provider") {
      setError(t("booking.compactBookingForm.errorProvider"));
      return;
    }
    
    if (!isAuthenticated) {
      console.log('[CompactBookingForm] 🔐 User not authenticated - saving pending data');
      const bookingData = {
        selectedDate: formData.booking_hours[0]?.date,
        selectedDateFormatted: formData.booking_hours[0]?.date,
        selectedDuration,
        startTime: formData.booking_hours[0]?.from,
        endTime: formData.booking_hours[0]?.to,
        capacity,
        isCoworking,
        spaceId: effectiveSpace?.id || space?.id,
        utmParams: utmParams,
        action: 'addToBasket',
        isModal: isModal,
        timestamp: Date.now(),
        bookingHours: formData.booking_hours
      };
      savePendingData(bookingData);
      let loginSuccess = false;
      const handleLoginSuccess = () => {
        loginSuccess = true;
        closeModal();
      };
      openModal({ 
        content: <Login 
          forBookingForm={true} 
          spaceId={effectiveSpace?.id || space?.id}
          onSuccess={handleLoginSuccess}
        />, 
        title: t("modal.login.title"),
        size: "small",
        onClose: () => {
          if (!loginSuccess) {
            clearPendingData();
          }
        }
      });
      return;
    }
    console.log('[CompactBookingForm] ✅ User authenticated - creating booking');
    const bookingData = {
      selectedDate: formData.booking_hours[0]?.date,
      selectedDateFormatted: formData.booking_hours[0]?.date,
      selectedDuration,
      startTime: formData.booking_hours[0]?.from,
      endTime: formData.booking_hours[0]?.to,
      capacity,
      isCoworking,
      spaceId: effectiveSpace?.id || space?.id,
      utmParams: utmParams,
      action: 'addToBasket',
      isModal: isModal,
      timestamp: Date.now(),
      bookingHours: formData.booking_hours
    };
    await createBooking(bookingData, false, isModal);
  };

  const handleAskQuestion = () => {
    openModal({
      content: <RequestQuotationForm spaceId={effectiveSpace?.id || space?.id} spaceType={effectiveSpace?.placeUsage?.key || space?.placeUsage?.key} spaceData={effectiveSpace || space} />,
      title: t("booking.compactBookingForm.askQuestion"),
      size: "medium",
    });
  };

  const executeReservation = async () => {
    if (isExecutingRef.current || isNavigatingRef.current) return;
    console.log('[CompactBookingForm] 📋 executeReservation called');
    const bookingData = {
      selectedDate: formData.booking_hours[0]?.date,
      selectedDateFormatted: formData.booking_hours[0]?.date,
      selectedDuration,
      startTime: formData.booking_hours[0]?.from,
      endTime: formData.booking_hours[0]?.to,
      capacity,
      isCoworking,
      spaceId: effectiveSpace?.id || space?.id,
      utmParams: utmParams,
      action: 'reserve',
      isModal: isModal,
      timestamp: Date.now(),
      bookingHours: formData.booking_hours
    };
    await createBooking(bookingData, false, isModal);
  };

  // ============================================================
  // handleReserve - Updated for multiple slots
  // ============================================================
  const handleReserve = async () => {
    console.log('[CompactBookingForm] 📋 handleReserve called');
    
    setError(null);
    setFieldErrors({});
    
    bookingCompletedRef.current = false;
    bookingSuccessfullyCompletedRef.current = false;
    isBookingCompleteRef.current = false;
    isProcessingCompleteRef.current = false;
    sessionStorage.removeItem(STORAGE_KEYS.BOOKING_COMPLETED);
    sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_STARTED);
    sessionStorage.removeItem(STORAGE_KEYS.PROCESSING);
    sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_MSG);
    sessionStorage.removeItem(STORAGE_KEYS.PROCESSING_IN_PROGRESS);
    sessionStorage.removeItem(BOOKING_COMPLETED_SUCCESS_KEY);
    sessionStorage.removeItem(RESTORATION_DONE_KEY);
    sessionStorage.removeItem(SUBMITTED_FORM_KEY);
    setIsProcessing(false);
    setProcessingMessage("");
    setProcessingStep(0);
    
    if (!formData.booking_hours || formData.booking_hours.length === 0) {
      setFieldErrors(prev => ({ ...prev, date: t("booking.compactBookingForm.atLeastOneSlotRequired") }));
      return;
    }
    
    for (const slot of formData.booking_hours) {
      if (!slot.date || !slot.from || !slot.to) {
        setFieldErrors(prev => ({ ...prev, date: t("booking.compactBookingForm.invalidSlot") }));
        return;
      }
    }
    
    if (!capacity || capacity < 1) {
      setFieldErrors(prev => ({ ...prev, capacity: t("booking.compactBookingForm.capacityRequired") }));
      return;
    }
    
    if (isTrainee && !effectiveSpace?.trainee_bookable) {
      setError(t("booking.compactBookingForm.traineeNotAllowed"));
      return;
    }
    if (userType === "provider") {
      setError(t("booking.compactBookingForm.errorProvider"));
      return;
    }
    
    if (!isAuthenticated) {
      console.log('[CompactBookingForm] 🔐 User not authenticated - saving pending data');
      const bookingData = {
        selectedDate: formData.booking_hours[0]?.date,
        selectedDateFormatted: formData.booking_hours[0]?.date,
        selectedDuration,
        startTime: formData.booking_hours[0]?.from,
        endTime: formData.booking_hours[0]?.to,
        capacity,
        isCoworking,
        spaceId: effectiveSpace?.id || space?.id,
        utmParams: utmParams,
        action: 'reserve',
        isModal: isModal,
        timestamp: Date.now(),
        bookingHours: formData.booking_hours
      };
      savePendingData(bookingData);
      let loginSuccess = false;
      const handleLoginSuccess = () => {
        loginSuccess = true;
        closeModal();
      };
      openModal({ 
        content: <Login 
          forBookingForm={true} 
          spaceId={effectiveSpace?.id || space?.id}
          onSuccess={handleLoginSuccess}
        />, 
        title: t("modal.login.title"),
        size: "small",
        onClose: () => {
          if (!loginSuccess) {
            clearPendingData();
          }
        }
      });
      return;
    }
    console.log('[CompactBookingForm] ✅ User authenticated - executing reservation');
    await executeReservation();
  };

  const handleCapacityChange = (newCapacity) => {
    setCapacity(newCapacity);
    setFieldErrors(prev => ({ ...prev, capacity: null }));
  };

  // ============================================================
  // Calendar helpers
  // ============================================================
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const getDateStatus = useCallback((date) => {
    if (!date) return 'empty';
    const stableDate = createStableLocalDate(date);
    if (!stableDate) return 'empty';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minAllowedDate = getMinAllowedDate();
    if (stableDate < minAllowedDate) return 'disabled';
    if (stableDate < today) return 'past';
    const isWorking = isWorkingDay(stableDate);
    if (!isWorking) return 'unavailable';
    const isToday = stableDate.getTime() === today.getTime();
    if (isToday) return 'today';
    return 'available';
  }, [isWorkingDay, getMinAllowedDate]);

  const daysInMonth = getDaysInMonth(currentMonth);

  const bookingCompleted = sessionStorage.getItem(BOOKING_COMPLETED_SUCCESS_KEY) === 'true' || 
                           bookingSuccessfullyCompletedRef.current;

  const showButtonLoading = isProcessing && !bookingCompleted;

  const isFormValidForAddToBasket = formData.booking_hours.length > 0 && 
    userType !== "provider" && 
    !(isTrainee && !effectiveSpace?.trainee_bookable);

  const hasMultipleSlots = formData.booking_hours.length > 1;

  const FormLoadingState = () => {
    const show = isProcessing && !bookingCompleted;
    if (!show) return null;
    return (
      <div className={styles.formLoadingState} key="loading-overlay">
        <div className={styles.loadingCard}>
          <div className={styles.spinner}></div>
          <div className={styles.loadingText}>
            <strong>{processingMessage}</strong>
            <p>{t("booking.compactBookingForm.doNotClose")}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`${styles.compactBookingForm} ${className || ""}`}>
      <FormLoadingState />
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>{t("booking.compactBookingForm.title")}</h2>
        </div>
      </div>
      <button 
        className={styles.phoneButton} 
        onClick={handlePhoneClick} 
        type="button"
        data-testid="call-button"
      >
        <svg 
          className={styles.phoneIcon} 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        +33 (0) 1 80 522 522
      </button>
      
<div className={styles.dateSection}>
  <div className={styles.datePickerWrapper}>
    <button 
      className={styles.dateButton} 
      onClick={() => setIsCalendarOpen(!isCalendarOpen)} 
      type="button" 
      disabled={showButtonLoading}
    >
      {(() => {
        if (selectedDate) {
          let displayText = `${formatDateForDisplay(selectedDate, currentLanguage).dayOfMonth} ${formatDateForDisplay(selectedDate, currentLanguage).monthYear}`;
          
          if (startTime && endTime) {
            displayText += ` • ${formatTimeDisplay(startTime)} - ${formatTimeDisplay(endTime)}`;
          }
          
          return (
            <span className={styles.selectedDate}>
              <span className={styles.dateDisplay}>
                {displayText}
              </span>
            </span>
          );
        }
        
        if (formData.booking_hours.length > 0) {
          return <SelectedSlotsSummary slotCount={formData.booking_hours.length} t={t} />;
        }
        
        return (
          <span className={styles.placeholderDate}>
            {t("booking.compactBookingForm.selectDate")}
          </span>
        );
      })()}
      
      <svg className={styles.calendarIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8 2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3 10H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
    
    {fieldErrors.date && <span className={styles.errorMessage}>{fieldErrors.date}</span>}
    {fieldErrors.duration && <span className={styles.errorMessage}>{fieldErrors.duration}</span>}
    {fieldErrors.startTime && <span className={styles.errorMessage}>{fieldErrors.startTime}</span>}
    

    
    {isCalendarOpen && (
      <div className={`${styles.calendarPopup} ${selectedDate ? styles.hasDateSelected : ''}`} ref={calendarRef}>
        <div className={styles.popupContent}>
          <div className={styles.calendarSection}>
            <div className={styles.calendarHeader}>
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>‹</button>
              <span>{currentMonth.toLocaleString(currentLanguage, { month: "long" })} {currentMonth.getFullYear()}</span>
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>›</button>
            </div>
            <div className={styles.weekdays}>
              {[
                t("days.short.monday", "Mon"),
                t("days.short.tuesday", "Tue"),
                t("days.short.wednesday", "Wed"),
                t("days.short.thursday", "Thu"),
                t("days.short.friday", "Fri"),
                t("days.short.saturday", "Sat"),
                t("days.short.sunday", "Sun")
              ].map(day => (
                <span key={day}>{day}</span>
              ))}
            </div>
            <div className={styles.days}>
              {loadingAvailability && (
                <div className={styles.loadingOverlay}>
                  <div className={styles.smallSpinner}></div>
                </div>
              )}
              {daysInMonth.map((date, idx) => {
                if (!date) return <span key={`empty-${idx}`} className={styles.emptyDay} />;
                const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
                const status = getDateStatus(date);
                const isSelected = selectedDate && isSameDate(date, selectedDate);
                const isDisabled = status === 'disabled' || status === 'past';
                return (
                  <button
                    key={dateKey}
                    className={`${styles.day} ${styles[status]} ${isSelected ? styles.selected : ""} ${isDisabled ? styles.disabled : ""}`}
                    onClick={() => (status === 'available' || status === 'today') && handleDateSelect(date)}
                    disabled={isDisabled || showButtonLoading}
                    type="button"
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
          
          {selectedDate && (
            <div className={styles.selectionSection}>
              <div className={styles.durationField}>
                <label>{t("booking.compactBookingForm.duration")}</label>
                {loadingAvailability ? (
                  <p className={styles.loadingMessage}>{t("booking.compactBookingForm.loadingDurations")}</p>
                ) : availableDurationOptions.length > 0 ? (
                  <div className={styles.durationOptions}>
                    {availableDurationOptions.map(opt => (
                      <button
                        key={opt.value}
                        className={`${styles.durationChip} ${selectedDuration === opt.value ? styles.selected : ""}`}
                        onClick={() => handleDurationSelect(opt.value)}
                        disabled={showButtonLoading}
                        type="button"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className={styles.noDurationsMessage}>{t("booking.compactBookingForm.noDurations")}</p>
                )}
              </div>
              
              {selectedDuration && (
                <div className={styles.timeField}>
                  <label>{t("booking.compactBookingForm.startTime")}</label>
                  {loadingAvailability ? (
                    <p className={styles.loadingMessage}>{t("booking.compactBookingForm.loadingTimeSlots")}</p>
                  ) : availableSlots.length > 0 ? (
                    <div className={styles.timeOptions}>
                      {availableSlots.map(slot => (
                        <button
                          key={slot}
                          className={`${styles.timeChip} ${startTime === slot ? styles.selected : ""}`}
                          onClick={() => handleStartTimeSelect(slot)}
                          disabled={showButtonLoading}
                          type="button"
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className={styles.noSlotsMessage}>{t("booking.compactBookingForm.noSlots")}</p>
                  )}
                </div>
              )}
              
              {selectedDuration && startTime && endTime && (
                <button 
                  className={styles.confirmButton} 
                  onClick={handleAddSlot} 
                  disabled={showButtonLoading || isAddingSlot}
                  type="button"
                >
                  {isAddingSlot ? t("booking.compactBookingForm.adding") : t("booking.compactBookingForm.addSlot")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    )}
  </div>
</div>

      {/* Selected Slots List - With Green Checkmark */}
      {formData.booking_hours.length > 0 && (
        <div className={styles.selectedSlotsList}>
          {formData.booking_hours.map((slot, index) => {
            const formattedFrom = formatTimeDisplay(slot.from);
            const formattedTo = formatTimeDisplay(slot.to);
            const dateDisplay = formatDateForDisplay(slot.date, currentLanguage);
            const duration = calculateDuration(formattedFrom, formattedTo);
            
            return (
              <div key={index} className={styles.selectedSlotItem}>
                <div className={styles.slotInfoWrapper}>
                  <svg 
                    className={styles.checkmarkIcon} 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12" cy="12" r="10" fill="#22c55e" />
                    <path 
                      d="M8 12L11 15L16 9" 
                      stroke="white" 
                      strokeWidth="2.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className={styles.slotInfo}>
                    {dateDisplay.dayOfMonth} {dateDisplay.monthYear}
                    {' • '}{formattedFrom} - {formattedTo}
                    <span className={styles.slotDuration}>
                      ({duration}h)
                    </span>
                  </span>
                </div>
                <button
                  className={styles.removeSlotBtn}
                  onClick={() => handleRemoveSlot(index)}
                  disabled={showButtonLoading}
                  type="button"
                >
                  ✕
                </button>
              </div>
            );
          })}
          <div className={styles.slotsCount}>
            {formData.booking_hours.length} {formData.booking_hours.length === 1 ? t("booking.compactBookingForm.slot") : t("booking.compactBookingForm.slots")}
          </div>
        </div>
      )}
      
               {/* Show "Choose your time slot" message when date is selected but no time slot chosen */}
    {selectedDate && !selectedDuration && !startTime && !isCalendarOpen && (
      <div className={styles.chooseTimeSlotWarningOutside}>
        <svg className={styles.warningIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <span>{t("booking.compactBookingForm.chooseTimeSlot")}</span>
      </div>
    )}
    
      <div className={`${styles.field} ${fieldErrors.capacity ? styles.hasError : ""}`}>
        <div className={styles.capacitySelector}>
          <span className={styles.capacityValue}>
            {capacity} {capacity === 1 ? t("booking.compactBookingForm.person") : t("booking.compactBookingForm.people")}
          </span>
          <div className={styles.capacityButtons}>
            <button className={styles.capacityBtn} onClick={() => handleCapacityChange(capacity - 1)} disabled={capacity <= 1 || showButtonLoading} type="button">-</button>
            <button className={styles.capacityBtn} onClick={() => handleCapacityChange(capacity + 1)} disabled={capacity >= maxCapacity || showButtonLoading} type="button">+</button>
          </div>
        </div>
        {fieldErrors.capacity && <span className={styles.errorMessage}>{fieldErrors.capacity}</span>}
      </div>

 
      
      <button 
        className={`${styles.reserveButton} ${showButtonLoading ? styles.loading : ""}`} 
        onClick={handleReserve} 
        disabled={showButtonLoading || formData.booking_hours.length === 0}
        type="button"
      >
        {showButtonLoading ? (
          <span className={styles.buttonLoadingContent}>
            <span className={styles.buttonSpinner}></span>
            <span>{processingMessage}</span>
          </span>
        ) : (
          t("booking.compactBookingForm.reserve")
        )}
      </button>
      
      {!isTrainee && <PricesTable space={effectiveSpace || space} direction="column" />}
      
      <div className={styles.taxNote}>
        {t("booking.compactBookingForm.taxNote")}<br />
        {t("booking.compactBookingForm.consultTerms")} <a href="/static/cgu" target="_blank">{t("booking.compactBookingForm.cgv")}</a>
      </div>
      
      <div className={styles.actionButtons}>
        <button className={styles.questionButton} onClick={handleAskQuestion} disabled={showButtonLoading}>
          {t("booking.compactBookingForm.askQuestion")}
          <svg className={styles.questionIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 13C12 13 12 11 12 11C12 10 12.5 9 13.5 9C14.5 9 15 10 15 11C15 12 14 13 13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M9 9C9 9 9.5 7 12 7C14.5 7 15 9 15 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button 
          className={styles.basketButton} 
          onClick={handleAddToBasketClick} 
          disabled={showButtonLoading || !isFormValidForAddToBasket}
        >
          {t("booking.compactBookingForm.addToBasket")}
        </button>
      </div>
      
      {error && <div className={styles.error}>{error}</div>}
      {fieldErrors.timeSlot && <div className={styles.error}>{fieldErrors.timeSlot}</div>}
      {isValidating && (
        <div className={styles.validatingHint}>
          <span className={styles.spinner}></span>
          {t("booking.compactBookingForm.validating")}
        </div>
      )}
    </div>
  );
};

export default CompactBookingForm;